import * as React from 'react'
import detectBrowser from 'utils/detectBrowser'

export type SlideDirection = 'pan-x' | 'pan-y' | null

type BodyPosition =
  | {
      position: string
    }
  | null
  | undefined

type TouchEventState = {
  slideDirection: SlideDirection
  touchStartX: number
  touchStartY: number
  previousBodyPosition?: BodyPosition
}

// locking the screen on events is pretty risky, so we only do it on firefox
const lockSlide = (
  e: TouchEvent,
  direction: SlideDirection,
  previousBodyPosition: BodyPosition
) => {
  let internalPreviousBodyPosition = previousBodyPosition
  // stops scrolling on safari, and most browsers
  e.stopPropagation()
  // stops scrolling on chrome and chromium
  e.preventDefault()
  // stops scrolling on firefox

  const browser = detectBrowser()

  // pretty aggressive, but it's the only way to stop scrolling on safari
  if (browser === 'Safari') {
    document.body.setAttribute('style', `touch-action:${direction};`)
    if (!previousBodyPosition) {
      internalPreviousBodyPosition = {
        position: document.body.style.position
      }

      // Update the dom inside an animation frame
      window.requestAnimationFrame(() => {
        document.body.style.position = 'fixed'
        // width is set to 100% to prevent the page from growing. There is no initial width on this element.
        document.body.style.width = '100%'
      })
    }
  }
  if (browser === 'Firefox' || browser === 'Safari') {
    document.body.setAttribute('style', 'overflow:hidden;')
    window.scrollTo(0, 0)
  }
  return internalPreviousBodyPosition
}

// this is called a lot, to attempt to recover from missed touchend events
const unlockSlide = (previousBodyPosition?: BodyPosition | null) => {
  const browser = detectBrowser()

  if (browser === 'Safari') {
    document.body.setAttribute('style', `touch-action:auto;`)
    // Restore styles
    document.body.style.position = previousBodyPosition?.position || ''
  }

  if (browser === 'Firefox' || browser === 'Safari') {
    document.body.setAttribute('style', 'overflow:hidden auto;')
  }
}
const getInitialState = (): TouchEventState => ({
  slideDirection: null,
  touchStartX: 0,
  touchStartY: 0,
  previousBodyPosition: null
})

export default function useTouchSlide(index, setIndex) {
  const holder = React.useRef<null | HTMLDivElement>(null)

  React.useEffect(() => {
    if (!holder.current) return

    // set states to recover from missed touchend events
    let touchEventState = getInitialState()
    unlockSlide()

    let touchMoveX: number
    let moveDistanceX: number

    let touchMoveY: number

    const touchstart = (e: TouchEvent) => {
      if (!holder.current) return
      // resetting to initial state
      touchEventState = getInitialState()
      unlockSlide()
      touchEventState = {
        ...touchEventState,
        touchStartX: e.touches[0].pageX,
        touchStartY: e.touches[0].clientY
      }
    }

    const touchmove = (e: TouchEvent) => {
      const { touchStartX, touchStartY, slideDirection } = touchEventState
      if (!holder.current) return

      touchMoveX = e.touches[0].pageX
      touchMoveY = e.touches[0].clientY

      // Calculate distance to translate holder.
      moveDistanceX = touchStartX - touchMoveX

      // Calculate distance moved on Y axis.
      const moveDistanceY = touchStartY - touchMoveY

      const checkSlide =
        slideDirection === null &&
        Math.abs(moveDistanceX) + Math.abs(moveDistanceY) > 20

      if (Math.abs(moveDistanceY) > Math.abs(moveDistanceX) && checkSlide) {
        touchEventState.slideDirection = 'pan-y'
      }

      // Prevent scrolling on Y axis.
      if (Math.abs(moveDistanceX) > Math.abs(moveDistanceY) && checkSlide) {
        touchEventState.slideDirection = 'pan-x'
      }

      if (slideDirection === 'pan-y') {
        holder.current.style.transform = `translate3d(0,0,0)`
      }

      if (slideDirection === 'pan-x') {
        touchEventState.previousBodyPosition = lockSlide(
          e,
          slideDirection,
          touchEventState.previousBodyPosition
        )
        holder.current.style.transform = `translate3d(${-moveDistanceX}px,0,0)`
      }
    }

    const touchend = () => {
      if (!holder.current) return
      const { width, height } = holder.current?.getBoundingClientRect() || {
        width: 0,
        height: 0
      }
      const thresholdX = width * 0.2
      const thresholdY = height * 0.2
      if (
        moveDistanceX > thresholdX &&
        touchEventState.slideDirection === 'pan-x'
      ) {
        setIndex(index + 1)
      } else if (
        moveDistanceX < -thresholdY &&
        touchEventState.slideDirection === 'pan-x'
      ) {
        setIndex(index - 1)
      }
      holder.current.style.transform = `translate3d(0,0,0)`

      // reset states on touchEnd
      touchEventState = getInitialState()
      unlockSlide()
    }

    // adding passive: false to prevent scrolling on chrome and chromium. e.preventDefault() is disabled otherwise
    holder.current.addEventListener('touchstart', touchstart, {
      passive: false
    })
    holder.current.addEventListener('touchmove', touchmove, { passive: false })
    holder.current.addEventListener('touchend', touchend, { passive: false })

    // setting up initial state
    touchEventState = getInitialState()

    return () => {
      if (!holder.current) return
      holder.current.removeEventListener('touchstart', touchstart)
      holder.current.removeEventListener('touchmove', touchmove)
      unlockSlide()
    }
  }, [index, setIndex])

  return { holder }
}
